import { Link, Navigate } from "react-router-dom";
import { Home as HomeIcon, Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Reveal } from "../components/Reveal";
import { LEGAL_PAGES } from "../data/legal";
import { PHONE, PHONE_HREF, DBA } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function LegalPage({ slug }) {
  const page = LEGAL_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  return (
    <div data-testid="legal-page">
      <Seo title={page.metaTitle} description={page.metaDescription} path={`/${slug}`} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="legal-hero">
          <div className="noise-overlay" />
          <div className="relative max-w-4xl mx-auto px-6 md:px-8 pt-16 pb-16 md:pt-20 md:pb-20">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <span className="text-white/90">{page.overline}</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">{page.overline}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tight">{page.h1}</h1>
            {page.lastModified && <p className="mt-4 text-white/60 text-sm">Last modified: {page.lastModified}</p>}
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-8">
            {page.intro && (
              <Reveal>
                <p className="text-lg text-slate-700 leading-relaxed mb-12">{page.intro}</p>
              </Reveal>
            )}
            <div className="space-y-12">
              {page.blocks.map((b, i) => (
                <Reveal key={i} delay={0.03}>
                  <article data-testid={`legal-block-${i}`}>
                    <h2 className="font-display font-bold text-2xl text-[#0b192c] tracking-tight mb-4">{b.h2}</h2>
                    <div className="space-y-4">
                      {b.paragraphs.map((p, j) => (
                        <p key={j} className="text-slate-600 leading-relaxed text-[17px]">{p}</p>
                      ))}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <div className="mt-16 rounded-2xl bg-[#f8f9fa] border border-black/5 p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div>
                <h3 className="font-display font-bold text-xl text-[#0b192c]">Questions about this policy?</h3>
                <p className="text-slate-600 mt-1">Our team is happy to help — reach out anytime.</p>
              </div>
              <a href={PHONE_HREF} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="legal-call-btn">
                <Phone className="w-4 h-4" /> Call {PHONE}
              </a>
            </div>

            <p className="mt-10 text-xs text-slate-400 leading-relaxed border-t border-slate-200 pt-6">{DBA}</p>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
